import { gql } from "@apollo/client";
import { useParams } from "react-router-dom";
import {
  useSchedulerLogViewQuery,
  useMostRecentSchedulerLogViewQuery,
  useRecentSchedulerLogIndexViewQuery,
} from "~/graphql/__generated__/graphql";
import JsonViewScreen from "~/components/debug/JsonViewScreen";
import { usePermissionOrRedirect, Permission } from "~/hooks/permissions";
import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Modal,
  CircularProgress,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

export default function SchedulerLogView() {
  usePermissionOrRedirect(Permission.debugApps, "/command");
  const { visitId } = useParams();

  if (!visitId) {
    return <JsonViewScreen title={"Must provide visitId"} subtitle={""} data={"{}"} loading={false} />;
  }

  const { data: schedulerLogData, loading: schedulerLogDataLoading } =
    useSchedulerLogViewQuery({
      variables: {
        visitId: parseInt(visitId || ""),
      },
    });
  const log = schedulerLogData?.schedulerLogSingle;

  const title = log || schedulerLogDataLoading ? `Scheduler log for visit ${visitId}` : `No scheduler log found for visit ${visitId}`
  const subtitle = log ? `${log?.createdAt} - ${log?.createdBy?.email || 'no user'} - Option chosen: ${log.chosenOptionIndex}` : ""
  return <JsonViewScreen title={title} subtitle={subtitle} data={log?.logData || "{}"} loading={schedulerLogDataLoading} />;
}

 gql`
  query SchedulerLogView($visitId: Int)
  @permission(requires: debugApps)
  {
    schedulerLogSingle(where: { visitId: { eq: $visitId } }) {
      visitId
      runId
      chosenOptionIndex
      logData
      createdAt
      createdBy {
        email
      }
    }
  }
`;

export function MostRecentSchedulerLogView() {
  usePermissionOrRedirect(Permission.debugApps, "/command");

  const { data: mostRecentSchedulerLogData, loading: mostRecentSchedulerLogDataLoading } =
    useMostRecentSchedulerLogViewQuery();
  const log = mostRecentSchedulerLogData?.schedulerLogRecent?.[0];

  const title = log || mostRecentSchedulerLogDataLoading ? `Most recent scheduler log` : `No scheduler logs found`
  const subtitle = log ? `${new Date(Number(log?.createdAt))} - ${log?.createdBy?.email || 'no user'} - Option chosen: ${log.chosenOptionIndex ?? 'none'}` : ""
  return <JsonViewScreen title={title} subtitle={subtitle} data={log?.logData || "{}"} loading={mostRecentSchedulerLogDataLoading} />;
}

 gql`
  query MostRecentSchedulerLogView
  @permission(requires: debugApps)
  {
    schedulerLogRecent(limit: 1) {
      runId
      chosenOptionIndex
      logData
      createdAt
      createdBy {
        email
      }
    }
  }
`;

export function RecentSchedulerLogIndexView() {
  usePermissionOrRedirect(Permission.debugApps, "/command");

  const LIMIT = 10;

  const [selectedLog, setSelectedLog] = useState<typeof logs[0] | null>(null);

  const [page, setPage] = useState(0);
  const [pageInput, setPageInput] = useState("");

  const offset = page * LIMIT;

  const [patientIdInput, setPatientIdInput] = useState("");
  const [patientIdFilter, setPatientIdFilter] = useState<number | null>(null);

  const [userIdInput, setUserIdInput] = useState("");
  const [userIdFilter, setUserIdFilter] = useState<number | null>(null);

  const { data, loading } = useRecentSchedulerLogIndexViewQuery({
    variables: { limit: LIMIT, offset, patientId: patientIdFilter, userId: userIdFilter },
    fetchPolicy: "network-only",
  });

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => p + 1);

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  // Whenever the filters change, reset page to 0
  useEffect(() => {
    setPage(0);
    setPageInput("");
  }, [patientIdFilter, userIdFilter]);

  const handlePageInputSubmit = () => {
    const num = parseInt(pageInput);
    if (!isNaN(num) && num > 0) {
      setPage(num - 1);
    }
    setPageInput("");
  };

  const handleFilterSubmit = () => {
    const patientId = parseInt(patientIdInput);
    setPatientIdFilter(!isNaN(patientId) ? patientId : null);

    const userId = parseInt(userIdInput);
    setUserIdFilter(!isNaN(userId) ? userId : null);
  };

  const logs = data?.schedulerLogRecent || [];

  const handleOpenLog = (log: typeof logs[0]) => setSelectedLog(log);
  const handleCloseLog = () => setSelectedLog(null);

  return (
    <Box sx={{ height: "100%", width: "100%", mt: 4, mb: 4 }}>
      <Grid container spacing={2}>
        <Grid component="div">
          <Paper sx={{ width: "100%", p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Recent Scheduler Logs
              </Typography>

              {/* Patient filter input */}
              <Box mb={2} display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  label="Patient ID"
                  value={patientIdInput}
                  onChange={(e) => setPatientIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleFilterSubmit();
                  }}
                />
                <TextField
                  size="small"
                  label="Scheduler User ID"
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleFilterSubmit();
                  }}
                />
                <Button variant="contained" size="small" onClick={handleFilterSubmit}>
                  Filter
                </Button>
                {(patientIdFilter !== undefined || userIdFilter !== undefined) && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPatientIdInput("");
                      setPatientIdFilter(null);
                      setUserIdInput("");
                      setUserIdFilter(null);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>User Email</TableCell>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>Option Chosen</TableCell>
                    <TableCell>Run Id</TableCell>
                    <TableCell>View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center" }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center" }}>
                        No logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log, idx) => (
                      <TableRow key={log.runId || idx}>
                        <TableCell>{new Date(Number(log.createdAt)).toLocaleString()}</TableCell>
                        <TableCell>{log.createdBy?.email || "Unknown"}</TableCell>
                        <TableCell>
                          {log.patient
                            ? `${log.patient.firstName} ${log.patient.lastName}`
                            : "---"}
                        </TableCell>
                        <TableCell>{log.chosenOptionIndex ?? "None"}</TableCell>
                        <TableCell>{log.runId}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => handleOpenLog(log)}>
                            View JSON
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination controls */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <Button variant="outlined" onClick={handlePrev} disabled={page === 0}>
                  Previous
                </Button>
                <Typography ml={0}>
                  Page {page + 1} {loading && "(loading...)"}
                </Typography>
                <Button variant="outlined" onClick={handleNext} disabled={logs.length < LIMIT}>
                  Next
                </Button>
              </Box>
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  value={pageInput}
                  onChange={handlePageInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handlePageInputSubmit();
                  }}
                  sx={{ width: 60 }}
                />
                <Button variant="outlined" onClick={handlePageInputSubmit}>
                  Jump to page
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Modal for full JSON view */}
      <Modal
        open={!!selectedLog}
        onClose={handleCloseLog}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: "90%",
            maxHeight: "90%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            overflowY: "auto",
          }}
        >
          {selectedLog && (
            <JsonViewScreen
              title={`Log ${selectedLog.runId}`}
              subtitle={`Created: ${new Date(
                Number(selectedLog.createdAt)
              ).toLocaleString()} by ${selectedLog.createdBy?.email}`}
              data={selectedLog.logData || "{}"}
              loading={false}
            />
          )}
          <Box sx={{ textAlign: "right", mt: 2 }}>
            <Button onClick={handleCloseLog}>Close</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

gql`
query recentSchedulerLogIndexView($limit: Int, $offset: Int, $patientId: Int, $userId: Int)
@permission(requires: debugApps)
{
  schedulerLogRecent(limit: $limit, offset: $offset, patientId: $patientId, userId: $userId) {
    runId
    chosenOptionIndex
    createdAt
    logData
    patient {
      firstName
      lastName
    }
    createdBy {
      email
    }
  }
}
`;
